<script setup lang="ts">
    import { ref, computed, onMounted } from 'vue'
    import { ellipsis, isNullOrWhitespace, strCompareLowerCase } from '@common/Common'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        CorrelationId: string
        Attributes: { AttributeId: number; Name: string; Section: string }[]
        SelectedAttributesIds: number[]
    }>()

    const emit = defineEmits<{
        (e: 'save', payload: { CorrelationId: string; SelectedAttributesIds: number[] }): void
        (e: 'close'): void
    }>()

    // ---------------- local state ----------------

    const attributes = ref<AttributeViewModel[]>([])
    const sections = ref<SectionViewModel[]>([])
    const searchPhrase = ref('') // ✅ NEVER nullable

    // ---------------- computed ----------------

    const sectionsView = computed(() =>
    {
        if (isNullOrWhitespace(searchPhrase.value))
        {
            return sections.value
        }

        return sections.value.filter(s => s.attributesView.length > 0)
    })

    const selectedAttributes = computed(() =>
        attributes.value.filter(a => a.IsSelected)
    )

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        attributes.value = props.Attributes.map(a => new AttributeViewModel(a))

        attributes.value
            .filter(a => props.SelectedAttributesIds.includes(a.AttributeId))
            .forEach(a => a.IsSelected = true)

        const sectionNames = attributes.value
            .map(a => a.Section)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .sort(strCompareLowerCase)

        sections.value = sectionNames.map(
            name => new SectionViewModel(
                name,
                attributes.value.filter(a => a.Section === name),
                searchPhrase
            )
        )
    })

    // ---------------- actions ----------------

    function save()
    {
        emit('save', {
            CorrelationId: props.CorrelationId,
            SelectedAttributesIds: selectedAttributes.value.map(a => a.AttributeId)
        })
    }

    function close()
    {
        emit('close')
    }

    // ---------------- local VMs ----------------

    class SectionViewModel
    {
        constructor(
            public Name: string,
            private Attributes: AttributeViewModel[],
            private searchPhrase: { value: string }
        ) { }

        get attributesView(): AttributeViewModel[]
        {
            const phrase = this.searchPhrase.value

            if (isNullOrWhitespace(phrase))
            {
                return this.Attributes
            }

            const lower = phrase.toLowerCase()
            return this.Attributes.filter(a =>
                `${a.Name} ${a.Section}`.toLowerCase().includes(lower)
            )
        }
    }

    class AttributeViewModel
    {
        AttributeId: number
        Name: string
        Section: string
        NameEllipsis: string
        IsSelected = false

        constructor(attr: { AttributeId: number; Name: string; Section: string })
        {
            this.AttributeId = attr.AttributeId
            this.Name = attr.Name
            this.Section = attr.Section
            this.NameEllipsis = ellipsis(this.Name, 52)
        }

        toggleSelect()
        {
            this.IsSelected = !this.IsSelected
        }
    }
</script>



<template>
    <div class="modal-mask">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content" style="height: calc(100vh - 50px);">
                    <div class="modal-header">
                        <h5 class="modal-title">Select attributes</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">

                        <div class="row">

                            <div class="col-md-6">
                                <div class="input-group">
                                    <input type="search" placeholder="Search..." class="form-control" v-model.trim="searchPhrase">
                                    <span class="input-group-append">
                                        <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                                    </span>
                                </div>
                                <div style="max-height: calc(100vh - 280px); overflow-y: auto;" class="mt-3">
                                    <div v-for="s in sectionsView" :key="s.Name">
                                        <h6 class="mt-3 mb-1">{{s.Name}}</h6>
                                        <div v-for="attr in s.attributesView" :key="attr.AttributeId">
                                            <a class="dropdown-item" @click="attr.toggleSelect()">
                                                <i class="fa-regular fa-lg mr-1" :class="{ 'fa-square-check': attr.IsSelected, 'fa-square': !attr.IsSelected}"></i>
                                                <span v-bind:title="attr.Name">{{attr.NameEllipsis}}</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            <div class="col-md-6">
                                <h5>Selected attributes ({{selectedAttributes.length}}):</h5>
                                <div style="max-height: calc(100vh - 280px); overflow-y: auto;">
                                    <div v-for="attr in selectedAttributes" :key="attr.AttributeId" class="p-2 bg-secondary m-2 text-white">
                                        <span v-bind:title="attr.Name">{{attr.NameEllipsis}}</span>
                                        <button type="button" class="p-0 border-0 text-white" style="background-color: transparent; font-size: 1.3rem; float: right;"
                                                @click="attr.toggleSelect()">
                                            &times;
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>

                    </div>

                    <div class="modal-footer">
                        <button @click="save" type="button" class="btn btn-primary">Save &amp; Close</button>
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>